import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const to = loc.state?.from?.pathname || "/dashboard";
      nav(to, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center px-6 py-12 fade-up">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <div className="flex items-center gap-2.5 mb-12">
              <ChurchFlowLogo size={44} className="soft-glow rounded-2xl" />
              <div className="font-serif text-2xl tracking-tight">ChurchFlow</div>
            </div>
            <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-foreground">
              Welcome back.
            </h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              One steady source of truth for your ministry's finances.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@church.org"
                required
                className="h-12 rounded-2xl bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 rounded-2xl bg-card"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full h-12 rounded-2xl soft-glow active:scale-[0.98] transition-transform group"
            >
              {submitting ? "Signing in…" : (
                <span className="inline-flex items-center gap-2">
                  Sign in
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              )}
            </Button>
            <p className="text-sm text-muted-foreground text-center pt-2">
              New here?{" "}
              <Link to="/register" data-testid="goto-register-link" className="text-primary underline-offset-4 hover:underline font-medium">
                Create an account
              </Link>
            </p>
          </form>

          <div className="rule-line mt-12" />
          <p className="mt-4 text-xs text-muted-foreground/80 leading-relaxed">
            <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
            <span className="mx-2 text-muted-foreground/50">·</span>
            Tithes, offerings, expenses — kept in order, kept in trust.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <Link to="/legal/terms" className="hover:text-foreground underline-offset-4 hover:underline" data-testid="login-terms-link">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-foreground underline-offset-4 hover:underline" data-testid="login-privacy-link">Privacy</Link>
            <Link to="/legal/ccpa" className="hover:text-foreground underline-offset-4 hover:underline" data-testid="login-ccpa-link">CCPA / CPRA</Link>
            <span className="opacity-70">© {new Date().getFullYear()} Ministry Branch</span>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden mesh-bg">
        <div className="absolute inset-0 flex items-center justify-center px-12">
          <div className="text-primary-foreground max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-xs uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> Stewardship platform
            </div>
            <div className="font-serif text-5xl mt-8 leading-[1.05] tracking-tight">
              Every dollar, accounted. Every donor, remembered.
            </div>
            <p className="mt-6 text-white/80 leading-relaxed text-lg">
              Track tithes, offerings, recurring expenses, and generate year-end giving statements in seconds.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 text-center">
              {[
                { k: "0%", v: "Transaction fees" },
                { k: "100%", v: "Audit-ready" },
                { k: "1-click", v: "PDF statements" },
              ].map((s) => (
                <div key={s.v} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-4">
                  <div className="font-serif text-2xl">{s.k}</div>
                  <div className="text-[11px] uppercase tracking-wider text-white/70 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
